import { ChevronRight, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/storefront/PageHeader";
import { ProductCard } from "@/components/storefront/ProductCard";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useSite } from "@/context/SiteContext";
import { API } from "@/lib/api";

export default function Shop() {
  const { categories, categoryTree } = useSite();
  const [params, setParams] = useSearchParams();
  const activeSlug = params.get("category") || "";
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("default");

  useEffect(() => {
    setProducts(null);
    API.get("/products", { params: activeSlug ? { category: activeSlug } : {} })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, [activeSlug]);

  const activeCat = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || null,
    [categories, activeSlug]
  );

  const visible = useMemo(() => {
    if (!products) return null;
    let list = products;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(needle) || (p.description || "").toLowerCase().includes(needle));
    }
    const priceOf = (p) => Number(p.sale_price || p.price);
    if (sort === "price-asc") list = [...list].sort((a, b) => priceOf(a) - priceOf(b));
    else if (sort === "price-desc") list = [...list].sort((a, b) => priceOf(b) - priceOf(a));
    else if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, q, sort]);

  const choose = (slug) => {
    if (slug) setParams({ category: slug });
    else setParams({});
  };

  return (
    <div data-testid="shop-page">
      <Seo title={`${activeCat ? activeCat.name + " | " : "Shop | "}Eileen Jenkins Florist`} description={activeCat?.description} />
      <PageHeader title={activeCat ? activeCat.name : "Shop"} subtitle={activeCat?.description || "Fresh flowers and hand-made tributes, arranged to order."} testId="shop-header" />

      <div className="max-w-7xl mx-auto px-4 pb-20">
        <div className="lg:hidden mb-6 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4" data-testid="category-chips">
          <button
            onClick={() => choose("")}
            data-testid="chip-all"
            className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${!activeSlug ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary"}`}
          >
            All
          </button>
          {categoryTree.map((p) => (
            <button
              key={p.id}
              onClick={() => choose(p.slug)}
              data-testid={`chip-${p.slug}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors ${activeSlug === p.slug ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary"}`}
            >
              {p.name}
            </button>
          ))}
        </div>

        <div className="flex gap-10">
          <aside className="hidden lg:block w-60 shrink-0" data-testid="category-sidebar">
            <button
              onClick={() => choose("")}
              data-testid="cat-all"
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!activeSlug ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}
            >
              All Products
            </button>
            {categoryTree.map((parent) => (
              <div key={parent.id} className="mt-3">
                <button
                  onClick={() => choose(parent.slug)}
                  data-testid={`cat-${parent.slug}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${activeSlug === parent.slug ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}
                >
                  {parent.name}
                </button>
                <div className="ml-3 border-l pl-2 mt-1 space-y-0.5">
                  {parent.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => choose(child.slug)}
                      data-testid={`cat-${child.slug}`}
                      className={`w-full text-left flex items-center gap-1 px-2 py-1.5 rounded-md text-sm transition-colors ${activeSlug === child.slug ? "text-primary font-medium" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      <ChevronRight className="h-3 w-3 shrink-0" /> {child.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search flowers…"
                  className="pl-9 rounded-full"
                  data-testid="shop-search-input"
                />
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-full sm:w-48 rounded-full" data-testid="shop-sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default" data-testid="sort-default">Featured order</SelectItem>
                  <SelectItem value="price-asc" data-testid="sort-price-asc">Price: low to high</SelectItem>
                  <SelectItem value="price-desc" data-testid="sort-price-desc">Price: high to low</SelectItem>
                  <SelectItem value="name" data-testid="sort-name">Name A–Z</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {visible === null ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="aspect-[4/5] rounded-xl" />)}
              </div>
            ) : visible.length === 0 ? (
              <div className="text-center py-20" data-testid="shop-empty">
                <p className="font-display text-2xl">No products found</p>
                <p className="text-muted-foreground mt-2">Try a different category or search term.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8" data-testid="product-grid">
                {visible.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
